import { useState, useEffect } from 'react'
import styled from 'styled-components'
import { api } from '../../services/api'
import { Spinner, LoadingContainer } from '../../components/shared/StyledComponents'
import { BillingAllocationTable } from './components/BillingAllocationTable'

const PageTitle = styled.h1`
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  font-size: ${({ theme }) => theme.fontSizes.xl};
`

const PageSubtitle = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: 600;
  margin: ${({ theme }) => theme.spacing.xl} 0 ${({ theme }) => theme.spacing.md} 0;
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 2px solid ${({ theme }) => theme.colors.secondary};
`

const Card = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`

const CardTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  font-size: ${({ theme }) => theme.fontSizes.md};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`

const CardDescription = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'warning' }>`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 600;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};

  ${({ $variant, theme }) => {
        switch ($variant) {
            case 'secondary':
                return `
          background: white;
          color: ${theme.colors.primary};
          border: 2px solid ${theme.colors.primary};
          &:hover { background: ${theme.colors.primary}; color: white; }
        `
            case 'warning':
                return `
          background: ${theme.colors.warning};
          color: white;
          border: none;
          &:hover { opacity: 0.9; }
        `
            default:
                return `
          background: ${theme.colors.primary};
          color: white;
          border: none;
          &:hover { opacity: 0.9; }
        `
        }
    }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const StatCard = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  text-align: center;
`

const StatValue = styled.div<{ $color?: string }>`
  font-size: ${({ theme }) => theme.fontSizes.xxl};
  font-weight: 700;
  color: ${({ $color, theme }) => $color || theme.colors.primary};
`

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: ${({ theme }) => theme.spacing.xs};
`

const UserList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`

const UserRow = styled.div<{ $pct: number; $isComplete?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ $pct, $isComplete }) =>
        $isComplete ? '#e8f5e9' : $pct >= 50 ? '#fff3e0' : '#ffebee'};

  &:last-child {
    border-bottom: none;
  }
`

const UserName = styled.span`
  font-weight: 500;
`

const UserPct = styled.span<{ $pct: number; $isComplete?: boolean }>`
  font-weight: 600;
  color: ${({ $pct, $isComplete }) =>
        $isComplete ? '#2e7d32' : $pct >= 50 ? '#ef6c00' : '#c62828'};
`

const Alert = styled.div<{ $type: 'success' | 'error' | 'info' }>`
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};

  ${({ $type }) => {
        switch ($type) {
            case 'success':
                return 'background: #e8f5e9; color: #2e7d32; border: 1px solid #a5d6a7;'
            case 'error':
                return 'background: #ffebee; color: #c62828; border: 1px solid #ef9a9a;'
            default:
                return 'background: #e3f2fd; color: #1565c0; border: 1px solid #90caf9;'
        }
    }}
`

interface ProfileStats {
    total_users: number
    total_categories: number
    users_complete: number
    users_below_75: number
    users_below_50: number
    users: Array<{ id: number; name: string; completion_pct: number; is_complete?: boolean }>
}

interface NotificationResult {
    message: string
    users_notified: number
    users?: Array<{ name: string; completion_pct?: number }>
}

export const AdminPage: React.FC = () => {
    const [stats, setStats] = useState<ProfileStats | null>(null)
    const [loading, setLoading] = useState(true)
    const [sending, setSending] = useState<'initial' | 'reminder' | null>(null)
    const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null)
    const [refreshKey, setRefreshKey] = useState(0)
    const [allUsers, setAllUsers] = useState<Array<{ id: number; username: string; display_name: string; department?: string; job_role?: string }>>([])
    const [deletingUserId, setDeletingUserId] = useState<number | null>(null)
    const [userSearchTerm, setUserSearchTerm] = useState('')

    // Force refresh on every mount (navigation to this page)
    useEffect(() => {
        setRefreshKey(prev => prev + 1)
    }, [])

    useEffect(() => {
        loadStats()
        loadAllUsers()
    }, [refreshKey])

    const loadAllUsers = async () => {
        try {
            const response = await api.users.getAll()
            const data = Array.isArray(response.data) ? response.data : []
            setAllUsers(data.filter((u: any) => (u.username || '').toLowerCase() !== 'admin'))
        } catch (error) {
            console.error('Error loading users:', error)
        }
    }

    const loadStats = async () => {
        try {
            setLoading(true)
            const response = await api.admin.getProfileStats()
            setStats(response.data)
        } catch (error) {
            console.error('Error loading stats:', error)
            setAlert({ type: 'error', message: 'Error al cargar estadísticas' })
        } finally {
            setLoading(false)
        }
    }

    const handleSendInitial = async () => {
        if (!confirm('¿Enviar email de bienvenida a TODOS los usuarios? Esta acción enviará un email a cada usuario del sistema.')) {
            return
        }

        try {
            setSending('initial')
            setAlert(null)
            const response = await api.admin.sendInitialNotifications()
            const result: NotificationResult = response.data
            setAlert({
                type: 'success',
                message: `✅ ${result.message}`,
            })
        } catch (error: any) {
            console.error('Error sending notifications:', error)
            const detail = error?.response?.data?.detail
            setAlert({ type: 'error', message: detail || 'Error al enviar notificaciones' })
        } finally {
            setSending(null)
        }
    }

    const handleSendReminder = async () => {
        const usersBelow = stats ? stats.users_below_75 + stats.users_below_50 : 0
        if (!confirm(`¿Enviar recordatorio a usuarios con menos del 75% completado? (${usersBelow} usuarios)`)) {
            return
        }

        try {
            setSending('reminder')
            setAlert(null)
            const response = await api.admin.sendReminderNotifications(75)
            const result: NotificationResult = response.data
            setAlert({
                type: 'success',
                message: `✅ ${result.message}`,
            })
        } catch (error: any) {
            console.error('Error sending reminders:', error)
            const detail = error?.response?.data?.detail
            setAlert({ type: 'error', message: detail || 'Error al enviar recordatorios' })
        } finally {
            setSending(null)
        }
    }

    if (loading) {
        return (
            <LoadingContainer>
                <Spinner />
            </LoadingContainer>
        )
    }

    return (
        <div>
            <PageTitle>⚙️ Configuración</PageTitle>
            <PageSubtitle>Panel de administración del sistema</PageSubtitle>

            {alert && (
                <Alert $type={alert.type}>
                    {alert.message}
                </Alert>
            )}

            <SectionTitle>📊 Estado de Perfiles de Conocimiento</SectionTitle>

            <StatsGrid>
                <StatCard>
                    <StatValue>{stats?.total_users || 0}</StatValue>
                    <StatLabel>Total Usuarios</StatLabel>
                </StatCard>
                <StatCard>
                    <StatValue $color="#2e7d32">{stats?.users_complete || 0}</StatValue>
                    <StatLabel>Perfiles Completos (≥75%)</StatLabel>
                </StatCard>
                <StatCard>
                    <StatValue $color="#ef6c00">{stats?.users_below_75 || 0}</StatValue>
                    <StatLabel>Entre 50% y 75%</StatLabel>
                </StatCard>
                <StatCard>
                    <StatValue $color="#c62828">{stats?.users_below_50 || 0}</StatValue>
                    <StatLabel>Menos del 50%</StatLabel>
                </StatCard>
            </StatsGrid>

            <SectionTitle>📧 Notificaciones por Email</SectionTitle>

            <Card>
                <CardTitle>👋 Envío Inicial (Primera llamada)</CardTitle>
                <CardDescription>
                    Envía un email de bienvenida a <strong>todos los usuarios</strong> del sistema,
                    invitándoles a completar su perfil de conocimientos. Usar solo la primera vez
                    o para nuevos usuarios.
                </CardDescription>
                <Button
                    onClick={handleSendInitial}
                    disabled={sending !== null}
                >
                    {sending === 'initial' ? (
                        <>
                            <Spinner style={{ width: 16, height: 16 }} /> Enviando...
                        </>
                    ) : (
                        '📤 Enviar Email Inicial a Todos'
                    )}
                </Button>
            </Card>

            <Card>
                <CardTitle>🔔 Recordatorio Mensual</CardTitle>
                <CardDescription>
                    Envía un recordatorio a usuarios que tengan <strong>menos del 75%</strong> de su
                    perfil completado. Actualmente hay <strong>{(stats?.users_below_75 || 0) + (stats?.users_below_50 || 0)} usuarios</strong> que
                    recibirían este recordatorio.
                </CardDescription>
                <Button
                    $variant="warning"
                    onClick={handleSendReminder}
                    disabled={sending !== null}
                >
                    {sending === 'reminder' ? (
                        <>
                            <Spinner style={{ width: 16, height: 16 }} /> Enviando...
                        </>
                    ) : (
                        '📧 Enviar Recordatorio (<75%)'
                    )}
                </Button>
            </Card>

            <SectionTitle>👥 Detalle por Usuario</SectionTitle>

            <Card>
                <CardDescription>
                    Lista de usuarios ordenados por porcentaje de completitud del perfil.
                    <br />
                    Completitud = CV subido (50%) + tecnologías rellenas (el otro 50%).
                    <br />
                    🟢 ≥75% | 🟠 50-74% | 🔴 &lt;50%. El recordatorio mensual se envía a los que están por debajo del 75%.
                </CardDescription>
                <UserList>
                    {stats?.users.map((user) => (
                        <UserRow key={user.id} $pct={user.completion_pct} $isComplete={user.is_complete}>
                            <UserName>{user.name}</UserName>
                            <UserPct $pct={user.completion_pct} $isComplete={user.is_complete}>{user.completion_pct}%{user.is_complete ? ' ✓' : ''}</UserPct>
                        </UserRow>
                    ))}
                </UserList>
            </Card>

            <SectionTitle>📊 Planificación de horas</SectionTitle>
            <BillingAllocationTable />

            <SectionTitle>🗑️ Gestión de Usuarios</SectionTitle>

            <Card>
                <CardTitle>Gestión de Usuarios</CardTitle>
                <CardDescription>
                    Administra permisos y usuarios del sistema. Puedes dar acceso a la vista de Infraestructura
                    o eliminar usuarios (eliminación lógica: soft delete).
                </CardDescription>
                <div style={{ marginBottom: '12px' }}>
                    <input
                        type="text"
                        placeholder="Buscar usuario por nombre..."
                        value={userSearchTerm}
                        onChange={(e) => setUserSearchTerm(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                        }}
                    />
                </div>
                <UserList>
                    {allUsers
                        .filter((u) =>
                            !userSearchTerm ||
                            (u.display_name || u.username || '').toLowerCase().includes(userSearchTerm.toLowerCase())
                        )
                        .map((user) => (
                            <UserRow key={user.id} $pct={100} style={{ background: 'white' }}>
                                <div style={{ flex: 1 }}>
                                    <UserName>{user.display_name || user.username}</UserName>
                                    {user.job_role && (
                                        <span style={{ fontSize: '0.8rem', color: '#666', marginLeft: '8px' }}>
                                            ({user.job_role})
                                        </span>
                                    )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Button
                                        $variant={(user as any).can_view_infra ? 'primary' : 'secondary'}
                                        onClick={async () => {
                                            try {
                                                await api.users.toggleInfraAccess(user.id)
                                                loadAllUsers()
                                            } catch (error) {
                                                console.error('Error toggling infra access:', error)
                                                setAlert({ type: 'error', message: 'Error al cambiar el acceso a infraestructura.' })
                                            }
                                        }}
                                        style={{ padding: '4px 10px', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                                    >
                                        {(user as any).can_view_infra ? '🖥️ Infra ✓' : '🖥️ Infra'}
                                    </Button>
                                    <Button
                                        $variant="warning"
                                        disabled={deletingUserId === user.id}
                                        onClick={async () => {
                                            if (!confirm(`¿Estás seguro de que quieres eliminar al usuario "${user.display_name || user.username}"? Esta acción es reversible desde la base de datos.`)) {
                                                return
                                            }
                                            try {
                                                setDeletingUserId(user.id)
                                                await api.users.delete(user.id)
                                                setAlert({ type: 'success', message: `Usuario "${user.display_name || user.username}" eliminado correctamente.` })
                                                loadAllUsers()
                                                loadStats()
                                            } catch (error) {
                                                console.error('Error deleting user:', error)
                                                setAlert({ type: 'error', message: `Error al eliminar al usuario.` })
                                            } finally {
                                                setDeletingUserId(null)
                                            }
                                        }}
                                        style={{ padding: '4px 12px', fontSize: '0.8rem' }}
                                    >
                                        {deletingUserId === user.id ? 'Eliminando...' : '🗑️ Eliminar'}
                                    </Button>
                                </div>
                            </UserRow>
                        ))}
                    {allUsers.filter((u) =>
                        !userSearchTerm ||
                        (u.display_name || u.username || '').toLowerCase().includes(userSearchTerm.toLowerCase())
                    ).length === 0 && (
                            <div style={{ padding: '16px', textAlign: 'center', color: '#999' }}>
                                No se encontraron usuarios
                            </div>
                        )}
                </UserList>
            </Card>
        </div>
    )
}

export default AdminPage
